/**
 * PILL FIELD — controllo "pill" del form crea task (nuovo design).
 *
 * Ogni pill mostra sempre ICONA + NOME DEL CAMPO + VALORE corrente, così è
 * auto-esplicativa (non un valore ambiguo). Al click apre un Popover MUI che
 * ospita il controllo vero e proprio (option list, time input, autocomplete):
 * la logica dei campi resta in TaskForm, la pill è solo il trigger + display.
 *
 * `PillOptions` è la lista di scelte usata dalle pill a valore discreto
 * (Tipo, Priorità, Scadenza): un click seleziona e chiude il popover.
 */

import { useState, type ReactNode } from 'react';
import Popover from '@mui/material/Popover';

interface PillFieldProps {
  icon: ReactNode;
  /** Nome del campo, sempre visibile (es. "Priorità"). */
  label: string;
  /** Valore corrente, mostrato in grassetto accanto al nome. */
  value: ReactNode;
  /** Contenuto del popover (il controllo reale). Riceve `close` per chiudersi. */
  children: (close: () => void) => ReactNode;
  /** Classe extra sulla pill (es. per il pallino priorità). */
  className?: string;
  /** Larghezza del popover (px). Default 240. */
  popoverWidth?: number;
}

export function PillField({ icon, label, value, children, className, popoverWidth = 240 }: PillFieldProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <button
        type="button"
        className={`tf-pill${className ? ` ${className}` : ''}`}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {icon}
        <span className="tf-pill__k">{label}</span>
        <span className="tf-pill__sep">·</span>
        <span className="tf-pill__v">{value}</span>
      </button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'tf-pop', sx: { width: popoverWidth } } }}
      >
        <div className="tf-pop__body">{children(close)}</div>
      </Popover>
    </>
  );
}

interface PillOption<V> {
  value: V;
  label: ReactNode;
  /**
   * Controllo affiancato alla voce, sulla stessa riga (es. il campo data della
   * scadenza "personalizzata"). Dove c'è, scegliere la voce NON chiude il
   * popover: la scelta non è ancora completa, manca il valore.
   */
  extra?: ReactNode;
}

interface PillOptionsProps<V> {
  options: PillOption<V>[];
  selected: V;
  onSelect: (value: V) => void;
  close: () => void;
}

/** Lista di scelte a valore discreto per una PillField. */
export function PillOptions<V extends string>({ options, selected, onSelect, close }: PillOptionsProps<V>) {
  return (
    <div className="tf-opts">
      {options.map((opt) => (
        <div key={opt.value} className="tf-opt-row">
          <button
            type="button"
            className={`tf-opt${opt.value === selected ? ' is-selected' : ''}`}
            onClick={() => { onSelect(opt.value); if (!opt.extra) close(); }}
          >
            {opt.label}
          </button>
          {opt.extra}
        </div>
      ))}
    </div>
  );
}
